import type { ReactNode } from 'react';
import { Alert, Badge, CopyButton, SideDrawer } from '@evogard/ui';
import type { FinanceExpenseBeneficiary } from '@evogard/contract';
import { formatDocument, formatInstant, formatPhone } from '@evogard/domain';
import {
  ACCOUNT_TYPE_LABELS,
  COMPANY_TYPE_LABELS,
  REF_TYPE_LABELS,
  bankLabel,
} from '../../../components/beneficiary/labels.js';
import { useFinanceBeneficiary } from './useFinanceDesk.js';

/**
 * A gaveta do beneficiário na mesa da despesa (28/10/2026, pedido do Paulo):
 * quem é o dono, como falar com ele e todas as contas que ele tem — a desta
 * despesa primeiro, as arquivadas por último.
 *
 * Abre no clique, e não com a mesa: para quem vê dados bancários, cada
 * abertura fica na trilha de auditoria (`dados_bancarios_vistos`).
 */
export function FinanceBeneficiaryDrawer({
  expenseId,
  onClose,
}: {
  expenseId: number;
  onClose: () => void;
}) {
  const query = useFinanceBeneficiary(expenseId);
  const data = query.data;
  const owner = data?.owner;
  const kind = owner
    ? owner.refType === 'companhia' && owner.companyType
      ? COMPANY_TYPE_LABELS[owner.companyType]
      : REF_TYPE_LABELS[owner.refType]
    : undefined;

  return (
    <SideDrawer
      eyebrow={kind ?? 'Beneficiário'}
      title={owner ? (owner.name ?? 'Dono não encontrado') : 'Carregando…'}
      closeLabel="Fechar o beneficiário"
      onClose={onClose}
    >
      {query.isError ? (
        <Alert tone="erro">{query.error.message}</Alert>
      ) : !data ? (
        <p className="text-sm text-texto-suave">Carregando o beneficiário…</p>
      ) : (
        <div className="flex flex-col gap-5 text-sm">
          <OwnerSection owner={data.owner} />
          <AccountsSection accounts={data.accounts} masked={data.masked} />
        </div>
      )}
    </SideDrawer>
  );
}

function OwnerSection({ owner }: { owner: FinanceExpenseBeneficiary['owner'] }) {
  if (!owner.readable) {
    return (
      <Alert tone="alerta">
        O cadastro do dono deste beneficiário não foi achado. As contas abaixo continuam
        valendo.
      </Alert>
    );
  }
  return (
    <section aria-label="Dados do dono" className="flex flex-col gap-2">
      <h4 className="text-xs font-semibold tracking-wide text-texto-tenue uppercase">
        Dados
      </h4>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
        <Row label="Documento">
          {owner.document ? formatDocument(owner.document) : null}
        </Row>
        {owner.code && <Row label="Código">{owner.code}</Row>}
        <Row label="E-mail">
          {owner.email ? <span className="break-all">{owner.email}</span> : null}
        </Row>
        <Row label="Telefone">{owner.phone ? formatPhone(owner.phone) : null}</Row>
        {owner.whatsapp && <Row label="WhatsApp">{formatPhone(owner.whatsapp)}</Row>}
        <Row label="Endereço">{owner.address}</Row>
      </dl>
    </section>
  );
}

function AccountsSection({
  accounts,
  masked,
}: {
  accounts: FinanceExpenseBeneficiary['accounts'];
  masked: boolean;
}) {
  return (
    <section aria-label="Contas" className="flex flex-col gap-2">
      <h4 className="text-xs font-semibold tracking-wide text-texto-tenue uppercase">
        Contas ({accounts.length})
      </h4>
      {masked && (
        <Alert tone="informacao">
          Agência e conta saem mascaradas: ver os dados bancários inteiros pede a
          permissão de ver dados bancários dos beneficiários.
        </Alert>
      )}
      <ul className="flex flex-col gap-3">
        {accounts.map((a) => (
          <li
            key={a.id}
            className="flex flex-col gap-2 rounded-borda border border-borda px-3 py-2.5"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-semibold">
                {bankLabel(a.bankCode, a.bankName) ?? 'Sem conta bancária'}
              </span>
              <span className="flex flex-wrap gap-1.5">
                {a.current && <Badge tone="informacao">Desta despesa</Badge>}
                {a.archivedAt && <Badge tone="neutro">Arquivada</Badge>}
              </span>
            </div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
              <Row label="Agência">
                {a.agency ? <span className="tabular-nums">{a.agency}</span> : null}
              </Row>
              <Row label="Conta">
                {a.accountNumber ? (
                  <span className="tabular-nums">
                    {a.accountNumber}
                    {a.accountType ? ` · ${ACCOUNT_TYPE_LABELS[a.accountType]}` : ''}
                  </span>
                ) : null}
              </Row>
              <Row label="Chave PIX">
                {a.pixKey ? (
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="break-all">{a.pixKey}</span>
                    <CopyButton text={a.pixKey} label="Copiar" />
                  </span>
                ) : null}
              </Row>
              <Row label="Titular">{a.accountHolderName}</Row>
              <Row label="CPF/CNPJ">{a.document ? formatDocument(a.document) : null}</Row>
              {a.archivedAt && (
                <Row label="Arquivada em">{formatInstant(a.archivedAt)}</Row>
              )}
            </dl>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Uma linha da ficha; o vazio diz "Não informado", e não some. */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-texto-suave">{label}</dt>
      <dd>{children ?? <span className="text-texto-tenue">Não informado</span>}</dd>
    </>
  );
}
